"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CsvParserStream = void 0;
const string_decoder_1 = require("string_decoder");
const stream_1 = require("stream");
const transforms_1 = require("./transforms");
const parser_1 = require("./parser");
class CsvParserStream extends stream_1.Transform {
  parserOptions;
  decoder;
  parser;
  headerTransformer;
  rowTransformerValidator;
  lines = '';
  rowCount = 0;
  parsedRowCount = 0;
  parsedLineCount = 0;
  endEmitted = false;
  headersEmitted = false;
  constructor(parserOptions) {
    super({
      objectMode: parserOptions.objectMode
    });
    this.parserOptions = parserOptions;
    this.parser = new parser_1.Parser(parserOptions);
    this.headerTransformer = new transforms_1.HeaderTransformer(parserOptions);
    this.decoder = new string_decoder_1.StringDecoder(parserOptions.encoding);
    this.rowTransformerValidator = new transforms_1.RowTransformerValidator();
  }
  get hasHitRowLimit() {
    return this.parserOptions.limitRows && this.rowCount >= this.parserOptions.maxRows;
  }
  get shouldEmitRows() {
    return this.parsedRowCount > this.parserOptions.skipRows;
  }
  get shouldSkipLine() {
    return this.parsedLineCount <= this.parserOptions.skipLines;
  }
  transform(transformFunction) {
    this.rowTransformerValidator.rowTransform = transformFunction;
    return this;
  }
  validate(validateFunction) {
    this.rowTransformerValidator.rowValidator = validateFunction;
    return this;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit(event) {
    if (event === 'end') {
      if (!this.endEmitted) {
        this.endEmitted = true;
        super.emit('end', this.rowCount);
      }
      return false;
    }
    for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      rest[_key - 1] = arguments[_key];
    }
    return super.emit(event, ...rest);
  }
  _transform(data, encoding, done) {
    // if we have hit our maxRows parsing limit then skip parsing
    if (this.hasHitRowLimit) {
      return done();
    }
    const wrappedCallback = CsvParserStream.wrapDoneCallback(done);
    try {
      const {
        lines
      } = this;
      const newLine = lines + this.decoder.write(data);
      const rows = this.parse(newLine, true);
      return this.processRows(rows, wrappedCallback);
    } catch (e) {
      return wrappedCallback(e);
    }
  }
  _flush(done) {
    const wrappedCallback = CsvParserStream.wrapDoneCallback(done);
    // if we have hit our maxRows parsing limit then skip parsing
    if (this.hasHitRowLimit) {
      return wrappedCallback();
    }
    try {
      const newLine = this.lines + this.decoder.end();
      const rows = this.parse(newLine, false);
      return this.processRows(rows, wrappedCallback);
    } catch (e) {
      return wrappedCallback(e);
    }
  }
  parse(data, hasMoreData) {
    if (!data) {
      return [];
    }
    const {
      line,
      rows
    } = this.parser.parse(data, hasMoreData);
    this.lines = line;
    return rows;
  }
  processRows(rows, cb) {
    const rowsLength = rows.length;
    const iterate = i => {
      const callNext = err => {
        if (err) {
          return cb(err);
        }
        if (i % 100 === 0) {
          // incase the transform are sync insert a next tick to prevent stack overflow
          setImmediate(() => {
            return iterate(i + 1);
          });
          return undefined;
        }
        return iterate(i + 1);
      };
      this.checkAndEmitHeaders();
      // if we have emitted all rows or we have hit the maxRows limit option
      // then end
      if (i >= rowsLength || this.hasHitRowLimit) {
        return cb();
      }
      this.parsedLineCount += 1;
      if (this.shouldSkipLine) {
        return callNext();
      }
      const row = rows[i];
      this.rowCount += 1;
      this.parsedRowCount += 1;
      const nextRowCount = this.rowCount;
      return this.transformRow(row, (err, transformResult) => {
        if (err) {
          this.rowCount -= 1;
          return callNext(err);
        }
        if (!transformResult) {
          return callNext(new Error('expected transform result'));
        }
        if (!transformResult.isValid) {
          this.emit('data-invalid', transformResult.row, nextRowCount, transformResult.reason);
        } else if (transformResult.row) {
          return this.pushRow(transformResult.row, callNext);
        }
        return callNext();
      });
    };
    iterate(0);
  }
  transformRow(parsedRow, cb) {
    try {
      this.headerTransformer.transform(parsedRow, (err, withHeaders) => {
        if (err) {
          return cb(err);
        }
        if (!withHeaders) {
          return cb(new Error('Expected result from header transform'));
        }
        if (!withHeaders.isValid) {
          if (this.shouldEmitRows) {
            return cb(null, {
              isValid: false,
              row: parsedRow
            });
          }
          // skipped because of skipRows option remove from total row count
          return this.skipRow(cb);
        }
        if (withHeaders.row) {
          if (this.shouldEmitRows) {
            return this.rowTransformerValidator.transformAndValidate(withHeaders.row, cb);
          }
          // skipped because of skipRows option remove from total row count
          return this.skipRow(cb);
        }
        // this is a header row dont include in the rowCount or parsedRowCount
        this.rowCount -= 1;
        this.parsedRowCount -= 1;
        return cb(null, {
          row: null,
          isValid: true
        });
      });
    } catch (e) {
      cb(e);
    }
  }
  checkAndEmitHeaders() {
    if (!this.headersEmitted && this.headerTransformer.headers) {
      this.headersEmitted = true;
      this.emit('headers', this.headerTransformer.headers);
    }
  }
  skipRow(cb) {
    // skipped because of skipRows option remove from total row count
    this.rowCount -= 1;
    return cb(null, {
      row: null,
      isValid: true
    });
  }
  pushRow(row, cb) {
    try {
      if (!this.parserOptions.objectMode) {
        this.push(JSON.stringify(row));
      } else {
        this.push(row);
      }
      cb();
    } catch (e) {
      cb(e);
    }
  }
  static wrapDoneCallback(done) {
    let errorCalled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (err) {
      if (err) {
        if (errorCalled) {
          throw err;
        }
        errorCalled = true;
        done(err);
        return;
      }
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      done(...args);
    };
  }
}
exports.CsvParserStream = CsvParserStream;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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